import React, { useState, useEffect } from 'react';
import './CommonStyles.css';
import './AddSellStyles.css';
import './ProductSearchStyles.css';
import './AddVendorModal.css';
import { PlusCircle } from 'lucide-react';
import apiService from '../../services/api';
import AddProduct from '../../AddProduct';
import dateUtils from '../../utils/dateUtils';


const now = () => dateUtils.formatDateTime(new Date());

// ─────────────────────────────────────────────────────────────────────────────
// AddVendorModal — defined OUTSIDE AddPurchase so React never unmounts it on
// parent state changes (prevents the blinking / remount bug).
// ─────────────────────────────────────────────────────────────────────────────
const AddVendorModal = ({
  vendorForm, vendorFormErrors, showVendorMoreInfo,
  onClose, onChange, onSubmit, onToggleMoreInfo,
}) => (
  <div className="modal-overlay add-vendor-modal-overlay" onClick={onClose}>
    <div className="add-vendor-modal-container" onClick={(e) => e.stopPropagation()}>
      <div className="add-vendor-modal-header">
        <h2>Add New Vendor</h2>
        <button type="button" className="add-vendor-close-btn" onClick={onClose}>✕</button>
      </div>

      <form onSubmit={onSubmit} noValidate>
        <div className="add-vendor-modal-body">

          {/* Row 1: Contact Type radios | Contact ID | Business Name (business only) */}
          <div className="av-main-row">
            <div className="av-group av-radio-cell">
              <div className="av-radio-group">
                <label className="av-radio-label">
                  <input type="radio" name="contact_type" value="individual"
                    checked={vendorForm.contact_type === 'individual'} onChange={onChange} />
                  Individual
                </label>
                <label className="av-radio-label">
                  <input type="radio" name="contact_type" value="business"
                    checked={vendorForm.contact_type === 'business'} onChange={onChange} />
                  Business
                </label>
              </div>
            </div>

            <div className="av-group">
              <label className="av-label">Contact ID:</label>
              <input type="text" name="contact_id" value={vendorForm.contact_id} onChange={onChange}
                placeholder="Contact ID" className="av-input" />
              <span className="av-hint">Leave empty to autogenerate</span>
            </div>

            {vendorForm.contact_type === 'business' && (
              <div className="av-group">
                <label className="av-label">Business Name:</label>
                <input type="text" name="business_name" value={vendorForm.business_name} onChange={onChange}
                  placeholder="Business Name" className="av-input" />
              </div>
            )}
          </div>

          {/* Row 2: Mobile | Alternate | Landline | Email */}
          <div className="av-row av-four-col av-mt">
            <div className="av-group">
              <label className="av-label">Mobile: <span className="av-required">*</span></label>
              <input type="text" name="mobile" value={vendorForm.mobile} onChange={onChange}
                placeholder="Mobile"
                className={`av-input${vendorFormErrors.mobile ? ' av-input-error' : ''}`} />
              {vendorFormErrors.mobile && <span className="av-error-text">{vendorFormErrors.mobile}</span>}
            </div>
            <div className="av-group">
              <label className="av-label">Alternate contact number:</label>
              <input type="text" name="alternate_contact_number"
                value={vendorForm.alternate_contact_number} onChange={onChange}
                placeholder="Alternate contact number" className="av-input" />
            </div>
            <div className="av-group">
              <label className="av-label">Landline:</label>
              <input type="text" name="landline" value={vendorForm.landline} onChange={onChange}
                placeholder="Landline" className="av-input" />
            </div>
            <div className="av-group">
              <label className="av-label">Email:</label>
              <input type="email" name="email" value={vendorForm.email} onChange={onChange}
                placeholder="Email" className="av-input" />
            </div>
          </div>

          {/* Row 3: Assigned To */}
          <div className="av-row av-mt">
            <div className="av-group av-assigned-group">
              <label className="av-label">Assigned to:</label>
              <select name="assigned_to" value={vendorForm.assigned_to} onChange={onChange} className="av-select">
                <option value="">Select User</option>
                <option value="Mr Ajay Gogia">Mr Ajay Gogia</option>
                <option value="ET Helpdesk">ET Helpdesk</option>
                <option value="MR Demo Demo">MR Demo Demo</option>
              </select>
            </div>
          </div>

          {/* More Informations button — purple, centered */}
          <div className="av-more-info-wrap">
            <button type="button" className="av-more-info-btn" onClick={onToggleMoreInfo}>
              More Informations {showVendorMoreInfo ? '▲' : '▼'}
            </button>
          </div>

          {/* Expandable More Info section */}
          {showVendorMoreInfo && (
            <div className="av-section av-mt">

              {/* Name fields */}
              <div className="av-row av-four-col">
                <div className="av-group" style={{ flex: '0 0 90px' }}>
                  <label className="av-label">Prefix:</label>
                  <select name="prefix" value={vendorForm.prefix} onChange={onChange} className="av-select">
                    <option value="">None</option>
                    <option value="Mr">Mr</option>
                    <option value="Mrs">Mrs</option>
                    <option value="Miss">Miss</option>
                  </select>
                </div>
                <div className="av-group">
                  <label className="av-label">First Name:</label>
                  <input type="text" name="first_name" value={vendorForm.first_name} onChange={onChange}
                    placeholder="First Name" className="av-input" />
                </div>
                <div className="av-group">
                  <label className="av-label">Last Name:</label>
                  <input type="text" name="last_name" value={vendorForm.last_name} onChange={onChange}
                    placeholder="Last Name" className="av-input" />
                </div>
              </div>

              {/* Tax / Opening Balance / Pay Term */}
              <div className="av-row av-four-col av-mt-sm">
                <div className="av-group">
                  <label className="av-label">Tax Number:</label>
                  <input type="text" name="tax_number" value={vendorForm.tax_number} onChange={onChange}
                    placeholder="Tax number" className="av-input" />
                </div>
                <div className="av-group">
                  <label className="av-label">Opening Balance:</label>
                  <input type="number" name="opening_balance" value={vendorForm.opening_balance} onChange={onChange}
                    placeholder="0" className="av-input" />
                </div>
                <div className="av-group">
                  <label className="av-label">Pay Term:</label>
                  <div style={{ display: 'flex', gap: '0.4rem' }}>
                    <input type="number" name="pay_term_value" value={vendorForm.pay_term_value} onChange={onChange}
                      placeholder="Val" className="av-input" style={{ flex: 1 }} />
                    <select name="pay_term_period" value={vendorForm.pay_term_period} onChange={onChange}
                      className="av-select" style={{ flex: 1 }}>
                      <option value="">Period</option>
                      <option value="Days">Days</option>
                      <option value="Months">Months</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Address */}
              <div className="av-row av-four-col av-mt-sm">
                <div className="av-group">
                  <label className="av-label">Address Line 1:</label>
                  <input type="text" name="address_line_1" value={vendorForm.address_line_1} onChange={onChange}
                    placeholder="Line 1" className="av-input" />
                </div>
                <div className="av-group">
                  <label className="av-label">Address Line 2:</label>
                  <input type="text" name="address_line_2" value={vendorForm.address_line_2} onChange={onChange}
                    placeholder="Line 2" className="av-input" />
                </div>
                <div className="av-group">
                  <label className="av-label">City:</label>
                  <input type="text" name="city" value={vendorForm.city} onChange={onChange}
                    placeholder="City" className="av-input" />
                </div>
                <div className="av-group">
                  <label className="av-label">State:</label>
                  <input type="text" name="state" value={vendorForm.state} onChange={onChange}
                    placeholder="State" className="av-input" />
                </div>
              </div>
              <div className="av-row av-four-col av-mt-sm">
                <div className="av-group">
                  <label className="av-label">Country:</label>
                  <input type="text" name="country" value={vendorForm.country} onChange={onChange}
                    placeholder="Country" className="av-input" />
                </div>
                <div className="av-group">
                  <label className="av-label">Zip Code:</label>
                  <input type="text" name="zip_code" value={vendorForm.zip_code} onChange={onChange}
                    placeholder="Zip Code" className="av-input" />
                </div>
                <div className="av-group">
                  <label className="av-label">Custom Field 1:</label>
                  <input type="text" name="custom_field_1" value={vendorForm.custom_field_1} onChange={onChange}
                    placeholder="Custom Field 1" className="av-input" />
                </div>
                <div className="av-group">
                  <label className="av-label">Custom Field 2:</label>
                  <input type="text" name="custom_field_2" value={vendorForm.custom_field_2} onChange={onChange}
                    placeholder="Custom Field 2" className="av-input" />
                </div>
              </div>
              <div className="av-row av-four-col av-mt-sm">
                <div className="av-group">
                  <label className="av-label">Custom Field 3:</label>
                  <input type="text" name="custom_field_3" value={vendorForm.custom_field_3} onChange={onChange}
                    placeholder="Custom Field 3" className="av-input" />
                </div>
                <div className="av-group">
                  <label className="av-label">Custom Field 4:</label>
                  <input type="text" name="custom_field_4" value={vendorForm.custom_field_4} onChange={onChange}
                    placeholder="Custom Field 4" className="av-input" />
                </div>
              </div>

              {/* Shipping Address */}
              <div className="av-mt-sm">
                <label className="av-label" style={{ display: 'block', marginBottom: '0.35rem' }}>Shipping Address:</label>
                <textarea name="shipping_address" value={vendorForm.shipping_address} onChange={onChange}
                  placeholder="Enter shipping address" className="av-input"
                  rows={3} style={{ resize: 'vertical', minHeight: '70px' }} />
              </div>
            </div>
          )}
        </div>

        {/* Footer — centered */}
        <div className="add-vendor-modal-footer">
          <button type="button" className="av-btn-cancel" onClick={onClose}>Close</button>
          <button type="submit" className="av-btn-save">Save Vendor</button>
        </div>
      </form>
    </div>
  </div>
);

const AddPurchase = ({ onBack, editData }) => {
  const [form, setForm] = useState({
    supplier: '',
    address: '',
    referenceNo: '',
    purchaseDate: now(),
    purchaseStatus: 'received',
    businessLocation: '',
    payTerm: '',
    payTermPeriod: 'Please Select',
    attachDocument: null,
    products: [],
    discountType: 'Percentage',
    discountAmount: '0',
    purchaseTax: '',
    additionalNotes: '',
    shippingDetails: '',
    shippingCharges: '0',
  });

  const [businessLocations, setBusinessLocations] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [isLoadingInitialData, setIsLoadingInitialData] = useState(false);

  const [showAddVendorModal, setShowAddVendorModal] = useState(false);

  // ── Add Vendor Modal state ───────────────────────────────────────────────────
  const initialVendorForm = {
    contact_type: 'individual',
    contact_id: '',
    first_name: '',
    last_name: '',
    prefix: '',
    business_name: '',
    mobile: '',
    alternate_contact_number: '',
    landline: '',
    email: '',
    tax_number: '',
    opening_balance: '0',
    pay_term_value: '',
    pay_term_period: '',
    address_line_1: '',
    address_line_2: '',
    city: '',
    state: '',
    country: '',
    zip_code: '',
    custom_field_1: '',
    custom_field_2: '',
    custom_field_3: '',
    custom_field_4: '',
    shipping_address: '',
    assigned_to: '',
  };
  const [vendorForm, setVendorForm] = useState(initialVendorForm);
  const [vendorFormErrors, setVendorFormErrors] = useState({});
  const [showVendorMoreInfo, setShowVendorMoreInfo] = useState(false);

  const handleVendorChange = (e) => {
    const { name, value } = e.target;
    setVendorForm(prev => ({ ...prev, [name]: value }));
    setVendorFormErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleVendorSubmit = (e) => {
    e.preventDefault();
    const errs = {};
    if (!vendorForm.first_name.trim()) errs.first_name = 'First name is required';
    if (!vendorForm.mobile.trim()) errs.mobile = 'Mobile is required';
    if (Object.keys(errs).length > 0) { setVendorFormErrors(errs); return; }
    const displayName = vendorForm.contact_type === 'business'
      ? (vendorForm.business_name || `${vendorForm.first_name} ${vendorForm.last_name}`).trim()
      : `${vendorForm.first_name} ${vendorForm.last_name}`.trim();
    handleChange('supplier', displayName);
    if (vendorForm.pay_term_value) {
      handleChange('payTerm', vendorForm.pay_term_value);
      if (vendorForm.pay_term_period) handleChange('payTermPeriod', vendorForm.pay_term_period);
    }
    setShowAddVendorModal(false);
    setVendorForm(initialVendorForm);
    setVendorFormErrors({});
    setShowVendorMoreInfo(false);
  };

  const handleCloseVendorModal = () => {
    setShowAddVendorModal(false);
    setVendorForm(initialVendorForm);
    setVendorFormErrors({});
    setShowVendorMoreInfo(false);
  };

  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [productError, setProductError] = useState('');
  const [displayLimit, setDisplayLimit] = useState(50);
  const [errors, setErrors] = useState({});
  const [showImportModal, setShowImportModal] = useState(false);
  const [importDragOver, setImportDragOver] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [isImportingProduct, setIsImportingProduct] = useState(false);
  const [importProductStatus, setImportProductStatus] = useState(null);
  const [isDownloadingProductTemplate, setIsDownloadingProductTemplate] = useState(false);
  const [showAddProductModal, setShowAddProductModal] = useState(false);

  // ── Attach Document preview state ─────────────────────────────────────────
  const [attachDocPreview, setAttachDocPreview] = useState(null);
  const [attachDocText, setAttachDocText] = useState('');
  const [showAttachDocModal, setShowAttachDocModal] = useState(false);
  const [attachDocZoom, setAttachDocZoom] = useState(1);

  // ── Payment entry state (same pattern as AddSell) ───────────────────────────
  const [payments, setPayments] = useState([]);
  const [paymentError, setPaymentError] = useState('');
  const [paymentEntry, setPaymentEntry] = useState({
    amount: '',
    paidOn: now(),
    paymentMethod: 'Cash',
    paymentAccount: '',
    paymentNote: '',
  });

  const handlePaymentEntryChange = (key, value) => {
    setPaymentEntry(prev => ({ ...prev, [key]: value }));
    if (key === 'amount') setPaymentError('');
  };

  const handleAddPayment = () => {
    if (!paymentEntry.amount || parseFloat(paymentEntry.amount) <= 0) {
      setPaymentError('Please enter a valid amount before adding payment.');
      return;
    }
    setPaymentError('');
    setPayments(prev => [...prev, { ...paymentEntry, id: Date.now() }]);
    setPaymentEntry({ amount: '', paidOn: now(), paymentMethod: 'Cash', paymentAccount: '', paymentNote: '' });
  };

  const handleRemovePayment = (id) => setPayments(prev => prev.filter(p => p.id !== id));

  const handleChange = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  // ── Derived totals ──────────────────────────────────────────────────────────
  const netTotal = form.products.reduce((s, p) => s + (parseFloat(p.lineTotal) || 0), 0);
  const discountVal = parseFloat(form.discountAmount) || 0;
  const discountAmt = form.discountType === 'Percentage' ? netTotal * discountVal / 100 : discountVal;
  const purchaseTotal = Math.max(0, netTotal - discountAmt + (parseFloat(form.shippingCharges) || 0));
  const totalPaid = payments.reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);
  const paymentDue = Math.max(0, purchaseTotal - totalPaid);
  const changeReturn = Math.max(0, totalPaid - purchaseTotal);

  // ── Auto-fill amount field when paymentDue changes ────────────────────────
  useEffect(() => {
    setPaymentEntry(prev => ({
      ...prev,
      amount: paymentDue > 0 ? paymentDue.toFixed(2) : ''
    }));
  }, [paymentDue]);

  // ── Initial Data Loading ───────────────────────────────────────────────────
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setIsLoadingInitialData(true);
        // Fetch Locations
        const locationsRes = await apiService.getBusinessLocations();
        const locations = locationsRes.data || locationsRes || [];
        setBusinessLocations(locations);
        
        // Fetch Suppliers
        const suppliersRes = await apiService.getContacts({ type: 'supplier' });
        const suppliersList = suppliersRes.data || suppliersRes || [];
        setSuppliers(suppliersList);

        // Auto-select first location only when NOT in edit mode
        if (locations.length > 0 && !editData) {
          setForm(prev => ({ ...prev, businessLocation: String(locations[0].id) }));
        }
      } catch (err) {
        console.error('Failed to load initial purchase data:', err);
      } finally {
        setIsLoadingInitialData(false);
      }
    };

    if (apiService.isTokenValid()) {
      fetchInitialData();
      loadProducts();
    }
  }, []);

  useEffect(() => {
    const loadEditData = async () => {
      if (!editData) return;
      try {
        // The GET response is { data: { id, purchase_lines, ... } }
        // Purchases.jsx unwraps .data before setting selectedPurchase, but guard here too.
        const d = editData.data ? editData.data : editData;

        const lines = (d.purchase_lines && d.purchase_lines.length > 0) 
          ? d.purchase_lines 
          : (Array.isArray(d.data) ? d.data : []);

        console.log('[AddPurchase] editData received. id=', d?.id,
          '| purchase_lines count=', d?.purchase_lines?.length,
          '| data array count=', (Array.isArray(d.data) ? d.data.length : 0),
          '| final lines to process=', lines.length);

        // Collect full product details for each line from the Product API
        const productPromises = lines.map(line => 
          apiService.getProduct(line.product_id).catch(err => {
            console.warn(`[AddPurchase] Failed to fetch product details for ID ${line.product_id}:`, err);
            return null;
          })
        );
        
        const fetchedProductsResults = await Promise.all(productPromises);
        const productMap = {};
        fetchedProductsResults.forEach(res => {
          if (!res) return;
          const pData = res.data || res;
          if (pData && pData.id) {
            productMap[String(pData.id)] = pData;
          }
        });

        const mappedProducts = lines.map(line => {
          // Historical data from the purchase line
          const priceBeforeDiscount = parseFloat(line.pp_without_discount || line.purchase_price || 0);
          const purchasePrice      = parseFloat(line.purchase_price || 0);
          const discountPercent    = parseFloat(line.discount_percent || 0);
          const qty                = parseFloat(line.quantity || 0);

          // item_tax is an absolute amount per unit – derive the % from purchase_price
          const itemTaxAbs = parseFloat(line.item_tax || 0);
          const taxPercent = purchasePrice > 0 ? (itemTaxAbs / purchasePrice) * 100 : 0;

          // Variations from the line (historical) or from master product (current)
          const lineVariation = line.variations || line.variation || {};
          const masterProduct = productMap[String(line.product_id)];
          const masterVariation = masterProduct?.product_variations?.[0]?.variations?.[0] || {};

          const unitCostBeforeTax = purchasePrice;
          const subtotalBeforeTax = unitCostBeforeTax * qty;
          const taxAmount         = unitCostBeforeTax * (taxPercent / 100);
          const netCost           = unitCostBeforeTax + taxAmount;
          const lineTotal         = netCost * qty;

          return {
            id: String(line.product_id) + '_' + line.id,
            product_id: line.product_id,
            purchase_line_id: line.id,
            variation_id: line.variation_id,
            // Use master product data for metadata if available
            name: masterProduct?.name || line.product?.name || ('Product ' + line.product_id),
            sku: masterProduct?.sku || line.product?.sku || lineVariation.sub_sku || masterVariation.sub_sku || '',
            purchaseQty: qty.toFixed(2),
            unitCostBeforeDiscount: priceBeforeDiscount.toFixed(2),
            discountPercent: discountPercent.toFixed(2),
            unitCostBeforeTax: unitCostBeforeTax.toFixed(2),
            subtotalBeforeTax: subtotalBeforeTax.toFixed(2),
            productTax: taxPercent.toFixed(2),
            productTaxId: line.tax_id || 1,
            netCost: netCost.toFixed(2),
            lineTotal: lineTotal.toFixed(2),
            // Use master variation for selling price details (current stats)
            profitMargin: parseFloat(masterVariation.profit_percent || lineVariation.profit_percent || 0).toFixed(2),
            unitSellingPrice: parseFloat(masterVariation.sell_price_inc_tax || lineVariation.sell_price_inc_tax || 0).toFixed(2),
            previousUnitPrice: priceBeforeDiscount,
            previousDiscount: discountPercent,
          };
        });

        console.log('[AddPurchase] mappedProducts with master data:', mappedProducts.length, 'items');

        setForm({
          supplier: String(d.contact_id || d.supplier_id || ''),
          address: d.contact?.address_line_1 || '',
          referenceNo: d.ref_no || '',
          purchaseDate: d.transaction_date || now(),
          purchaseStatus: d.status || 'received',
          businessLocation: String(d.location_id || ''),
          payTerm: d.pay_term_number != null ? String(d.pay_term_number) : '',
          payTermPeriod: d.pay_term_type
            ? d.pay_term_type.charAt(0).toUpperCase() + d.pay_term_type.slice(1)
            : 'Please Select',
          attachDocument: null,
          products: mappedProducts,
          discountType: d.discount_type
            ? d.discount_type.charAt(0).toUpperCase() + d.discount_type.slice(1)
            : 'Percentage',
          discountAmount: String(parseFloat(d.discount_amount || 0)),
          purchaseTax: d.tax_id ? String(d.tax_id) : '',
          additionalNotes: d.additional_notes || '',
          shippingDetails: d.shipping_details || '',
          shippingCharges: String(parseFloat(d.shipping_charges || 0)),
        });

        // Map existing payment lines
        const pmtLines = d.payment_lines || [];
        if (pmtLines.length > 0) {
          setPayments(pmtLines.map(p => ({
            id: p.id || Date.now() + Math.random(),
            payment_id: p.id,
            amount: String(p.amount),
            paymentMethod:
              p.method === 'cash'          ? 'Cash'          :
              p.method === 'card'          ? 'Card'          :
              p.method === 'bank_transfer' ? 'Bank Transfer' : 'Other',
            paidOn: p.paid_on || now(),
            paymentAccount: p.account_id ? String(p.account_id) : '',
            paymentNote: p.note || '',
          })));
        }
      } catch (err) {
        console.error('[AddPurchase] Error loading edit data:', err);
      }
    };

    loadEditData();
  }, [editData]);



  const loadProducts = async () => {
    try {
      setIsLoadingProducts(true);
      setProductError('');
      const data = await apiService.getProducts();
      const list = data.data || data || [];
      setProducts(list);
      setFilteredProducts(list);
    } catch (err) {
      setProductError('Failed to load products');
    } finally {
      setIsLoadingProducts(false);
    }
  };

  const handleProductSearch = async (term) => {
    setProductSearchTerm(term);
    setDisplayLimit(50);
    try {
      setIsLoadingProducts(true);
      setProductError('');
      const data = await apiService.getProducts(term);
      let list = data.data || data || [];
      if (term.trim() && list.length > 0) {
        const lower = term.toLowerCase().trim();
        list = list.filter(p => {
          const name = (p.product || p.name || p.product_name || p.title || '').toLowerCase();
          const sku = (p.sku || p.code || '').toLowerCase();
          return name.includes(lower) || sku.includes(lower);
        });
      }
      setProducts(list);
      setFilteredProducts(list);
      setShowProductDropdown(true);
    } catch (err) {
      setProductError('Failed to search products');
    } finally {
      setIsLoadingProducts(false);
    }
  };

  const handleProductSelect = (product) => {
    const variation = product.product_variations?.[0]?.variations?.[0] || {};
    const purchasePrice = parseFloat(
      product.purchase_price ||
      variation.default_purchase_price ||
      product.cost ||
      0
    );
    const unitSellingPrice = parseFloat(
      product.unit_price ||
      product.price ||
      variation.sell_price_inc_tax ||
      product.sell_price_inc_tax ||
      0
    );
    const name = product.product || product.name || product.product_name || product.title || 'Unknown Product';
    const qty = 1;
    const disc = 0;

    // Extract tax or derive it from dpp_inc_tax and default_purchase_price
    let tax = parseFloat(product.tax || product.tax_percent || 0);
    const netCostAPI = parseFloat(variation.dpp_inc_tax || 0);
    if (!tax && netCostAPI > purchasePrice && purchasePrice > 0) {
      tax = ((netCostAPI - purchasePrice) / purchasePrice) * 100;
    }

    // Profit margin from variation, or derive if search endpoint trimmed it
    let profitMargin = parseFloat(variation.profit_percent || product.profit_percent || product.margin || 0);
    if (!profitMargin && unitSellingPrice > 0 && netCostAPI > 0) {
      profitMargin = ((unitSellingPrice - netCostAPI) / netCostAPI) * 100;
    } else if (!profitMargin && unitSellingPrice > 0 && purchasePrice > 0) {
      profitMargin = ((unitSellingPrice - purchasePrice) / purchasePrice) * 100;
    }

    const unitCostBeforeTax = purchasePrice * (1 - disc / 100);
    const subtotalBeforeTax = unitCostBeforeTax * qty;
    const taxAmount = unitCostBeforeTax * tax / 100;
    const netCost = unitCostBeforeTax + taxAmount;
    const lineTotal = subtotalBeforeTax + (subtotalBeforeTax * tax / 100);
    // Use a unique key combining product_id + timestamp to avoid key collisions
    // when the same product is added multiple times
    const uniqueKey = `${product.id}_${Date.now()}`;
    setForm(prev => ({
      ...prev,
      products: [...prev.products, {
        id: uniqueKey,
        product_id: product.id,
        variation_id: product.variation_id || (product.product_variations?.[0]?.variations?.[0]?.id) || product.id,
        name,
        sku: product.sku || product.code || '',
        purchaseQty: qty.toFixed(2),
        unitCostBeforeDiscount: purchasePrice.toFixed(2),
        discountPercent: disc.toFixed(2),
        unitCostBeforeTax: unitCostBeforeTax.toFixed(2),
        subtotalBeforeTax: subtotalBeforeTax.toFixed(2),
        productTax: tax.toFixed(2),
        netCost: netCost.toFixed(2),
        lineTotal: lineTotal.toFixed(2),
        profitMargin: profitMargin.toFixed(2),
        unitSellingPrice: unitSellingPrice.toFixed(2),
        previousUnitPrice: purchasePrice,
        previousDiscount: disc,
      }],
    }));
    setProductSearchTerm('');
    setShowProductDropdown(false);
  };

  const handleProductInputFocus = () => {
    if (products.length > 0) {
      setFilteredProducts(products);
      setShowProductDropdown(true);
    } else {
      loadProducts();
      setShowProductDropdown(true);
    }
  };

  const handleProductInputBlur = () => {
    setTimeout(() => setShowProductDropdown(false), 150);
  };

  const updateProduct = (id, key, value) => {
    setForm(prev => ({
      ...prev,
      products: prev.products.map(p => {
        if (p.id !== id) return p;
        const updated = { ...p, [key]: value };
        const qty = parseFloat(updated.purchaseQty) || 0;
        const cost = parseFloat(updated.unitCostBeforeDiscount) || 0;
        const disc = parseFloat(updated.discountPercent) || 0;
        const tax = parseFloat(updated.productTax) || 0;
        const unitCostBeforeTax = cost * (1 - disc / 100);
        const subtotalBeforeTax = unitCostBeforeTax * qty;
        const taxAmt = subtotalBeforeTax * tax / 100;
        const lineTotal = subtotalBeforeTax + taxAmt;
        return { ...updated, unitCostBeforeTax: unitCostBeforeTax.toFixed(2), subtotalBeforeTax: subtotalBeforeTax.toFixed(2), netCost: (unitCostBeforeTax + unitCostBeforeTax * tax / 100).toFixed(2), lineTotal: lineTotal.toFixed(2) };
      }),
    }));
  };

  const removeProduct = (id) => setForm(prev => ({ ...prev, products: prev.products.filter(p => p.id !== id) }));

  // ── CSV / XLSX Import helpers ────────────────────────────────────────────────
  const handleDownloadImportProductTemplate = () => {
    // Serve the real template file placed in /public
    const a = document.createElement('a');
    a.href = `${process.env.PUBLIC_URL}/import_products_template.xls`;
    a.download = 'import_products_template.xls';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleImport = async () => {
    if (!importFile) {
      setImportProductStatus({ type: 'error', msg: 'Please select a file to import.', errors: [] });
      return;
    }

    const formData = new FormData();
    formData.append('file', importFile);

    try {
      setIsImportingProduct(true);
      setImportProductStatus(null);
      const res = await apiService.importProducts(formData);
      
      if (res.success) {
        setImportProductStatus({ 
          type: 'success', 
          msg: res.message || 'Products imported successfully', 
          data: res.data,
          errors: res.data?.errors || []
        });
        setImportFile(null);
        const fInput = document.getElementById('import-modal-file');
        if (fInput) fInput.value = '';
        
        // Refresh local product list so they can search for new items
        loadProducts();
      } else {
        setImportProductStatus({ 
          type: 'error', 
          msg: res.message || 'Import failed', 
          errors: res.data?.errors || [] 
        });
      }
    } catch (err) {
      setImportProductStatus({ type: 'error', msg: err.message, errors: [] });
    } finally {
      setIsImportingProduct(false);
    }
  };

  const handleSave = async () => {
    const newErrors = {};
    if (!form.supplier.trim()) newErrors.supplier = 'Supplier is required';
    if (!form.purchaseDate.trim()) newErrors.purchaseDate = 'Purchase date is required';
    if (!form.purchaseStatus) newErrors.purchaseStatus = 'Purchase status is required';
    if (!form.businessLocation) newErrors.businessLocation = 'Business location is required';
    if (form.products.length === 0) newErrors.products = 'At least one product is required';
    setErrors(newErrors);
    
    if (Object.keys(newErrors).length > 0) {
      alert('Please fill out all required fields.');
      return;
    }

    const formatToBackendDate = (dateStr) => {
      try {
        // Handle MM/DD/YYYY HH:MM AM/PM or similar
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return dateStr;
        
        const pad = (n) => String(n).padStart(2, '0');
        const y = d.getFullYear();
        const m = pad(d.getMonth() + 1);
        const day = pad(d.getDate());
        const h = pad(d.getHours());
        const min = pad(d.getMinutes());
        const s = pad(d.getSeconds());
        
        return `${y}-${m}-${day} ${h}:${min}:${s}`;
      } catch (e) {
        return dateStr;
      }
    };

    try {
      // Map products to what the backend expects.
      // We include both `products` and `purchases` arrays, and standard Ultimate POS keys,
      // because Ultimate POS expects `purchases` and `pp_without_discount`.
      // The backend validation strictly requires these exact keys,
      // and it requires a tax_id integer (default 1) or it will silently drop the array!
      const mappedLines = form.products.map(p => {
        const realProductId = p.product_id || parseInt(p.id, 10) || p.id;
        const realVariationId = p.variation_id || realProductId;
        
        const productEntry = {
          product_id: realProductId,
          variation_id: realVariationId,
          quantity: parseFloat(p.purchaseQty),
          unit_price_before_discount: parseFloat(p.unitCostBeforeDiscount),
          discount_percent: parseFloat(p.discountPercent) || 0,
          unit_price: parseFloat(p.unitCostBeforeTax),
          tax_id: parseInt(p.productTaxId || p.tax_id || p.tax, 10) || 1, // REQUIRED!
          profit_margin_percent: parseFloat(p.profitMargin) || 0,
          selling_price: parseFloat(p.unitSellingPrice)
        };
        
        if (p.purchase_line_id) {
          productEntry.purchase_line_id = p.purchase_line_id; // For updates
        }
        return productEntry;
      });

      const payload = {
        location_id: parseInt(form.businessLocation, 10),
        supplier_id: parseInt(form.supplier, 10), 
        transaction_date: formatToBackendDate(form.purchaseDate),
        ref_no: form.referenceNo || undefined,
        status: form.purchaseStatus,
        payment_status: paymentDue === 0 ? 'paid' : (payments.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0) > 0 ? 'partial' : 'due'),
        final_total: purchaseTotal,
        pay_term_number: form.payTerm ? parseInt(form.payTerm, 10) : undefined,
        pay_term_type: form.payTermPeriod !== 'Please Select' ? form.payTermPeriod.toLowerCase() : undefined,
        discount_type: form.discountType.toLowerCase(),
        discount_amount: parseFloat(form.discountAmount) || 0,
        tax_id: parseInt(form.purchaseTax, 10) || undefined,
        shipping_charges: parseFloat(form.shippingCharges) || 0,
        shipping_details: form.shippingDetails,
        additional_notes: form.additionalNotes,
        
        // Send ONLY the products array like it worked originally
        products: mappedLines,

        payments: payments.map(p => {
          const paymentEntry = {
            amount: parseFloat(p.amount) || 0,
            method: (p.paymentMethod || 'Cash').toLowerCase().replace(' ', '_'),
            paid_on: p.paidOn,
            note: p.paymentNote || '',
            account_id: p.paymentAccount ? parseInt(p.paymentAccount, 10) : undefined
          };
          // Include payment_id for existing payments so backend can update instead of creating duplicates
          if (p.payment_id) {
            paymentEntry.payment_id = p.payment_id;
          }
          return paymentEntry;
        })
      };

      console.log('Sending Purchase Payload:', JSON.stringify(payload, null, 2));

      const result = editData
        ? await apiService.updatePurchase(editData.id, payload)
        : await apiService.createPurchase(payload);

      console.log('💎 BINGO! Backend save API responded with:');
      console.log('Result Object:', result);
      console.log('Result JSON string:', JSON.stringify(result, null, 2));

      // Treat as success if we get any non-error response.
      // Some Laravel endpoints return { data: {...} }, others return { success:true },
      // others may return the record directly. Only throw if there's an explicit error field.
      const hasExplicitError = result?.error || (result?.success === false);
      if (hasExplicitError) {
        throw new Error(
          result.error?.message || result.message || 'Server reported failure'
        );
      }

      // Success — show feedback and navigate back so list refreshes
      alert(editData ? 'Purchase updated successfully!' : 'Purchase saved successfully!');
      if (onBack) onBack();

    } catch (err) {
      alert('Error saving purchase: ' + err.message);
      console.error(err);
    }
  };

  return (
    <div className="sell-form-container add-sell-override">
      <div className="page-header">
        <button
          type="button"
          onClick={onBack}
          style={{
            background: 'white',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            padding: '0.5rem 0.75rem',
            cursor: 'pointer',
            fontSize: '1.2rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '40px',
            height: '40px'
          }}
          title="Back to Purchases"
        >
          ←
        </button>
        <h2 style={{ fontSize: '1.25rem' }}>Add Purchase</h2>
      </div>

      <form className="sell-form" onSubmit={(e) => { e.preventDefault(); handleSave(); }}>

        {/* ── Section 1: Purchase Info ─────────────────────────────────────── */}
        <div className="form-section-content">
          <div className="form-row">
            <div className="form-group">
            <label>Supplier: <span style={{ color: '#ef4444' }}>*</span></label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <select
                className={`form-select ${errors.supplier ? 'error' : ''}`}
                value={form.supplier}
                onChange={(e) => {
                  const val = e.target.value;
                  handleChange('supplier', val);
                  if (val) {
                    const selectedVendor = suppliers.find(s => String(s.id) === String(val));
                    if (selectedVendor) {
                      // Construct a reasonable display address from available fields
                      const parts = [
                        selectedVendor.address_line_1,
                        selectedVendor.address_line_2,
                        selectedVendor.city,
                        selectedVendor.state,
                        selectedVendor.zip_code
                      ].filter(Boolean);
                      handleChange('address', parts.join(', '));

                      // Auto-fill pay term rules
                      if (selectedVendor.pay_term_number != null) {
                        handleChange('payTerm', String(selectedVendor.pay_term_number));
                      } else {
                        handleChange('payTerm', '');
                      }
                      
                      if (selectedVendor.pay_term_type) {
                        const ptType = selectedVendor.pay_term_type;
                        handleChange('payTermPeriod', ptType.charAt(0).toUpperCase() + ptType.slice(1).toLowerCase());
                      } else {
                        handleChange('payTermPeriod', 'Please Select');
                      }
                    }
                  } else {
                    handleChange('address', '');
                    handleChange('payTerm', '');
                    handleChange('payTermPeriod', 'Please Select');
                  }
                }}
                style={{ flex: 1 }}
                disabled={isLoadingInitialData}
              >
                <option value="">{isLoadingInitialData ? 'Loading Suppliers...' : 'Please Select'}</option>
                {suppliers.map(s => (
                  <option key={s.id} value={s.id}>{s.name} ({s.supplier_business_name || 'N/A'})</option>
                ))}
              </select>
                <button
                  type="button"
                  onClick={() => setShowAddVendorModal(true)}
                  title="Add New Vendor"
                  style={{
                    width: '32px',
                    height: '38px',
                    minWidth: '32px',
                    flexShrink: 0,
                    background: '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '1.2rem',
                    fontWeight: '700',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    lineHeight: 1,
                    padding: 0,
                  }}
                >
                  +
                </button>
              </div>
              {errors.supplier && <span className="error-text">{errors.supplier}</span>}
            </div>

            <div className="form-group">
              <label>Address:</label>
              <input
                type="text"
                className="form-input"
                value={form.address}
                onChange={(e) => handleChange('address', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Reference No:</label>
              <input
                type="text"
                className="form-input"
                value={form.referenceNo}
                onChange={(e) => handleChange('referenceNo', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label>Purchase Date: <span style={{ color: '#ef4444' }}>*</span></label>
              <input
                type="text"
                className={`form-input ${errors.purchaseDate ? 'error' : ''}`}
                value={form.purchaseDate}
                onChange={(e) => handleChange('purchaseDate', e.target.value)}
              />
              {errors.purchaseDate && <span className="error-text">{errors.purchaseDate}</span>}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Purchase Status: <span style={{ color: '#ef4444' }}>*</span></label>
              <select
                className={`form-select ${errors.purchaseStatus ? 'error' : ''}`}
                value={form.purchaseStatus}
                onChange={(e) => handleChange('purchaseStatus', e.target.value)}
              >
                <option value="">Please Select</option>
                <option value="new">New</option>
                <option value="received">Received</option>
                <option value="pending">Pending</option>
                <option value="ordered">Ordered</option>
              </select>
              {errors.purchaseStatus && <span className="error-text">{errors.purchaseStatus}</span>}
            </div>

            <div className="form-group">
              <label>Business Location: <span style={{ color: '#ef4444' }}>*</span></label>
              <select
                className={`form-select ${errors.businessLocation ? 'error' : ''}`}
                value={form.businessLocation}
                onChange={(e) => handleChange('businessLocation', e.target.value)}
              >
                <option value="">Please Select</option>
                {businessLocations.map(loc => (
                  <option key={loc.id} value={loc.id}>{loc.name}</option>
                ))}
              </select>
              {errors.businessLocation && <span className="error-text">{errors.businessLocation}</span>}
            </div>

            <div className="form-group">
              <label>Pay term:</label>
              <div className="payterm-input-group">
                <input
                  type="number"
                  className="form-input payterm-input"
                  value={form.payTerm}
                  onChange={(e) => handleChange('payTerm', e.target.value)}
                  min="0"
                  placeholder="Pay term"
                />
                <select
                  className="form-select payterm-select"
                  value={form.payTermPeriod}
                  onChange={(e) => handleChange('payTermPeriod', e.target.value)}
                >
                  <option value="Please Select">Please Select</option>
                  <option value="Days">Days</option>
                  <option value="Months">Months</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label style={{ display: 'flex', gap: '4px', alignItems: 'baseline', flexWrap: 'wrap' }}>
                Attach Document:
                <span style={{ fontSize: '0.78rem', color: '#64748b', fontWeight: 400 }}>(EDI files supported)</span>
              </label>
              <input
                type="file"
                className="form-input"
                accept=".pdf,.csv,.zip,.doc,.docx,.jpeg,.jpg,.png,.edi,.x12,.txt,.xml,.json"
                onChange={(e) => {
                  const file = e.target.files[0] || null;
                  handleChange('attachDocument', file);
                  setAttachDocPreview(null);
                  setAttachDocText('');
                  
                  if (file) {
                    const reader = new FileReader();
                    reader.onloadend = () => {
                      setAttachDocPreview(reader.result);
                    };
                    reader.readAsDataURL(file);

                    // If it's a potential text/EDI file, also read as text
                    const isTxt = file.type.startsWith('text/') || 
                                  file.type === 'application/json' ||
                                  file.type === 'application/xml' ||
                                  /\.(edi|x12|txt|csv|xml|json)$/i.test(file.name);
                    
                    if (isTxt) {
                      const textReader = new FileReader();
                      textReader.onloadend = () => {
                        setAttachDocText(textReader.result);
                      };
                      textReader.readAsText(file);
                    }
                  }
                }}
              />
              <span className="file-hint">Max File size: 5MB &nbsp;|&nbsp; .pdf .csv .zip .doc .docx .jpeg .jpg .png .edi .x12 .txt .xml .json</span>
              {form.attachDocument && (
                <div style={{ marginTop: '10px' }}>
                  <button
                    type="button"
                    onClick={() => setShowAttachDocModal(true)}
                    style={{
                      padding: '6px 16px',
                      fontSize: '0.875rem',
                      backgroundColor: '#3b82f6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      fontWeight: '500'
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                      <circle cx="12" cy="12" r="3"></circle>
                    </svg>
                    View Uploaded File
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <hr className="section-divider" />

        {/* ── Section 2: Products ──────────────────────────────────────────── */}
        <div className="form-section-content">
          <div className="form-row">
            <div className="form-group full-width">
              <label>Enter Product name / SKU / Scan bar code</label>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
                <button
                  type="button"
                  onClick={() => setShowImportModal(true)}
                  style={{
                    padding: '0 1rem',
                    height: '38px',
                    background: '#6366f1',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.4rem'
                  }}
                >
                  Import Products
                </button>
                <div className="product-dropdown-container" style={{ flex: 1, position: 'relative' }}>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Enter Product name / SKU / Scan bar code"
                    value={productSearchTerm}
                    onChange={(e) => handleProductSearch(e.target.value)}
                    onFocus={handleProductInputFocus}
                    onBlur={handleProductInputBlur}
                    autoComplete="off"
                    style={{ paddingRight: '45px' }}
                  />
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      setShowAddProductModal(true);
                    }}
                    title="Add New Product"
                    style={{
                      position: 'absolute',
                      right: '8px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <PlusCircle size={24} fill="#3b82f6" color="white" />
                  </button>
                  {isLoadingProducts && (
                    <div className="product-loading">Loading products...</div>
                  )}
                  {showProductDropdown && !isLoadingProducts && (
                    <div className="product-dropdown">
                      {productError ? (
                        <div className="product-dropdown-item error">{productError}</div>
                      ) : filteredProducts.length > 0 ? (
                        <>
                          {filteredProducts.slice(0, displayLimit).map((product) => (
                            <div
                              key={product.id}
                              className="product-dropdown-item"
                              onMouseDown={(e) => { e.preventDefault(); handleProductSelect(product); }}
                            >
                              <div className="product-name">
                                {product.product || product.name || product.product_name || product.title || 'Unknown Product'}
                                {(product.sku || product.code) && (
                                  <span className="product-sku-inline"> • SKU: {product.sku || product.code}</span>
                                )}
                              </div>
                              <div className="product-details">
                                {(product.purchase_price || product.cost) && (
                                  <span className="product-price">Cost: ${product.purchase_price || product.cost}</span>
                                )}
                                {product.barcode && (
                                  <span className="product-barcode">Barcode: {product.barcode}</span>
                                )}
                              </div>
                            </div>
                          ))}
                          {filteredProducts.length > displayLimit && (
                            <div
                              className="product-dropdown-item show-more"
                              onMouseDown={(e) => { e.preventDefault(); setDisplayLimit(prev => prev + 50); }}
                            >
                              Show {Math.min(50, filteredProducts.length - displayLimit)} more products...
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="product-dropdown-item no-results">No products found</div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="product-table" style={{ overflowX: 'auto' }}>
            <table style={{ minWidth: '1100px' }}>
              <thead>
                <tr>
                  <th style={{ width: '3%' }}>#</th>
                  <th style={{ width: '13%' }}>Product Name</th>
                  <th style={{ width: '10%' }}>Purchase Qty</th>
                  <th style={{ width: '10%' }}>Unit Cost (Before Discount)</th>
                  <th style={{ width: '9%' }}>Discount %</th>
                  <th style={{ width: '8%' }}>Unit Cost (Before Tax)</th>
                  <th style={{ width: '8%' }}>Subtotal (Before Tax)</th>
                  <th style={{ width: '7%' }}>Product Tax</th>
                  <th style={{ width: '7%' }}>Net Cost</th>
                  <th style={{ width: '8%' }}>Line Total</th>
                  <th style={{ width: '7%' }}>Profit Margin %</th>
                  <th style={{ width: '10%' }}>Unit Selling Price (Inc. tax)</th>
                  <th style={{ width: '3%' }}></th>
                </tr>
              </thead>
              <tbody>
                {form.products.length === 0 ? (
                  <tr><td colSpan={13} className="no-products">No products added</td></tr>
                ) : (
                  form.products.map((p, idx) => (
                    <tr key={p.id} style={{ verticalAlign: 'top' }}>
                      <td style={{ textAlign: 'center', color: '#64748b', fontSize: '0.8rem', paddingTop: '0.5rem' }}>{idx + 1}</td>
                      <td style={{ paddingTop: '0.5rem' }}>
                        <div style={{ fontWeight: '500', fontSize: '0.875rem', color: '#1e293b' }}>{p.name}</div>
                        {p.sku && <div style={{ fontSize: '0.75rem', color: '#64748b' }}>({p.sku})</div>}
                        <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.2rem' }}>Current stock: {parseFloat(p.current_stock || 1).toFixed(2)} Single</div>
                      </td>
                      <td style={{ paddingTop: '0.5rem' }}>
                        <input type="text" className="form-input" value={p.purchaseQty} onChange={(e) => updateProduct(p.id, 'purchaseQty', e.target.value)} onBlur={(e) => updateProduct(p.id, 'purchaseQty', parseFloat(e.target.value || 0).toFixed(2))} style={{ width: '100%', maxWidth: '80px', padding: '0.4rem', textAlign: 'left' }} />
                        <select className="form-select" style={{ marginTop: '0.5rem', width: '100%', maxWidth: '80px', padding: '0.2rem', fontSize: '0.75rem' }}>
                          <option>Single</option>
                        </select>
                      </td>
                      <td style={{ paddingTop: '0.5rem' }}>
                        <input type="text" className="form-input unit-price-input" value={p.unitCostBeforeDiscount} onChange={(e) => updateProduct(p.id, 'unitCostBeforeDiscount', e.target.value)} onBlur={(e) => updateProduct(p.id, 'unitCostBeforeDiscount', parseFloat(e.target.value || 0).toFixed(2))} style={{ width: '100%', maxWidth: '100px', minWidth: '75px', padding: '0.4rem 0.3rem', textAlign: 'left' }} />
                        <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.4rem', lineHeight: '1.2' }}>Previous unit<br />price: $ {(parseFloat(p.previousUnitPrice) || 0).toFixed(2)}</div>
                      </td>
                      <td style={{ paddingTop: '0.5rem' }}>
                        <input type="text" className="form-input" value={p.discountPercent} onChange={(e) => updateProduct(p.id, 'discountPercent', e.target.value)} onBlur={(e) => updateProduct(p.id, 'discountPercent', parseFloat(e.target.value || 0).toFixed(2))} style={{ width: '100%', maxWidth: '80px', minWidth: '60px', padding: '0.4rem 0.3rem', textAlign: 'left' }} />
                        <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.4rem', lineHeight: '1.2' }}>Previous<br />discount: {(parseFloat(p.previousDiscount) || 0).toFixed(2)}%</div>
                      </td>
                      <td style={{ paddingTop: '0.5rem' }}>
                        <input type="number" className="form-input" value={parseFloat(p.unitCostBeforeTax || 0).toFixed(2)} readOnly style={{ width: '100%', maxWidth: '85px', minWidth: '70px', padding: '0.4rem 0.3rem' }} />
                      </td>
                      <td style={{ color: '#374151', fontSize: '0.85rem', textAlign: 'center', paddingRight: '0.5rem', paddingTop: '0.5rem' }}>
                        <div style={{ padding: '0.5rem 0', marginTop: '1px' }}>{parseFloat(p.subtotalBeforeTax || 0).toFixed(2)}</div>
                      </td>
                      <td style={{ paddingTop: '0.5rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.2rem', maxWidth: '100px' }}>
                          <select className="form-select" style={{ padding: '0.2rem', width: '30px', cursor: 'pointer' }} onChange={(e) => updateProduct(p.id, 'productTax', e.target.value)}>
                            <option value={p.productTax}>{p.productTax ? '✔' : ''}</option>
                            <option value="0">None</option>
                          </select>
                          <input type="text" className="form-input" value={(parseFloat(p.subtotalBeforeTax || 0) * parseFloat(p.productTax || 0) / 100).toFixed(2)} readOnly style={{ width: '100%', minWidth: '45px', background: '#f8fafc', padding: '0.4rem' }} />
                        </div>
                      </td>
                      <td style={{ paddingTop: '0.5rem' }}>
                        <input type="number" className="form-input" value={parseFloat(p.netCost || 0).toFixed(2)} readOnly style={{ width: '100%', maxWidth: '85px', minWidth: '70px', padding: '0.4rem 0.3rem', textAlign: 'center' }} />
                      </td>
                      <td style={{ color: '#1e293b', fontWeight: '600', fontSize: '0.85rem', textAlign: 'center', paddingRight: '0.5rem', paddingTop: '0.5rem' }}>
                        <div style={{ padding: '0.5rem 0', marginTop: '1px' }}>{parseFloat(p.lineTotal || 0).toFixed(2)}</div>
                      </td>
                      <td style={{ paddingTop: '0.5rem' }}>
                        <input type="text" className="form-input" value={p.profitMargin} onChange={(e) => updateProduct(p.id, 'profitMargin', e.target.value)} onBlur={(e) => updateProduct(p.id, 'profitMargin', parseFloat(e.target.value || 0).toFixed(2))} style={{ width: '100%', maxWidth: '90px', minWidth: '75px', padding: '0.4rem 0.3rem', textAlign: 'left' }} />
                      </td>
                      <td style={{ paddingTop: '0.5rem' }}>
                        <input type="text" className="form-input no-spinners" value={p.unitSellingPrice} onChange={(e) => updateProduct(p.id, 'unitSellingPrice', e.target.value)} onBlur={(e) => updateProduct(p.id, 'unitSellingPrice', parseFloat(e.target.value || 0).toFixed(2))} style={{ width: '100%', maxWidth: '100px', minWidth: '75px', padding: '0.4rem 0.3rem', WebkitAppearance: 'none', MozAppearance: 'textfield', textAlign: 'left' }} />
                      </td>
                      <td style={{ textAlign: 'center', paddingTop: '0.5rem' }}>
                        <button type="button" className="remove-product-btn" onClick={() => removeProduct(p.id)}>✕</button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="product-summary">
            <span>Total Items: {form.products.length} ({form.products.reduce((s, p) => s + (parseFloat(p.purchaseQty) || 0), 0).toFixed(2)})</span>
            <span>Net Total Amount: {netTotal.toFixed(2)}</span>
          </div>
        </div>

        <hr className="section-divider" />

        {/* ── Section 3: Discount / Tax / Notes / Shipping ─────────────────── */}
        <div className="form-section-content">
          {/* Row 1: Discount Type, Discount Amount, Purchase Tax, Additional Notes */}
          <div className="form-row">
            <div className="form-group">
              <label>Discount Type:</label>
              <select className="form-select" value={form.discountType} onChange={(e) => handleChange('discountType', e.target.value)}>
                <option value="Percentage">Percentage</option>
                <option value="Fixed">Fixed</option>
              </select>
            </div>
            <div className="form-group">
              <label>Discount Amount:</label>
              <input type="number" className="form-input" value={form.discountAmount} min="0" step="0.01" onChange={(e) => handleChange('discountAmount', e.target.value)} />
              <div style={{ marginTop: '4px', fontSize: '0.82rem', color: '#64748b' }}>Discount: (-) $ {discountAmt.toFixed(2)}</div>
            </div>
            <div className="form-group">
              <label>Purchase Tax:</label>
              <select className="form-select" value={form.purchaseTax} onChange={(e) => handleChange('purchaseTax', e.target.value)}>
                <option value="">None</option>
                <option value="gst5">GST 5%</option>
                <option value="gst10">GST 10%</option>
                <option value="vat15">VAT 15%</option>
              </select>
              <div style={{ marginTop: '4px', fontSize: '0.82rem', color: '#64748b' }}>Purchase Tax: (+) 0.00</div>
            </div>
            <div className="form-group">
              <label>(+) Shipping Charges:</label>
              <input type="number" className="form-input" value={form.shippingCharges} min="0" step="0.01" onChange={(e) => handleChange('shippingCharges', e.target.value)} />
            </div>
          </div>
        </div>

        <hr className="section-divider" />

        {/* ── Section 4: Payment ───────────────────────────────────────────── */}
        <div className="form-section-content">
          <h3>Add payment</h3>
          <div className="payment-info">
            <span>Advance Balance: 0</span>
          </div>

          {/* Payment entry row */}
          <div className="form-row">
            <div className="form-group">
              <label>Amount: <span style={{ color: '#ef4444' }}>*</span></label>
              <input type="number" className={`form-input ${paymentError ? 'error' : ''}`} value={paymentEntry.amount} min="0" step="0.01" placeholder="0.00" onChange={(e) => handlePaymentEntryChange('amount', e.target.value)} />
              {paymentError && <span className="error-text" style={{ color: '#ef4444', fontSize: '0.8rem', marginTop: '4px', display: 'block' }}>{paymentError}</span>}
            </div>
            <div className="form-group">
              <label>Paid on: <span style={{ color: '#ef4444' }}>*</span></label>
              <input type="text" className="form-input" value={paymentEntry.paidOn} onChange={(e) => handlePaymentEntryChange('paidOn', e.target.value)} />
            </div>
            <div className="form-group">
              <label>Payment Method: <span style={{ color: '#ef4444' }}>*</span></label>
              <select className="form-select" value={paymentEntry.paymentMethod} onChange={(e) => handlePaymentEntryChange('paymentMethod', e.target.value)}>
                <option value="Cash">Cash</option>
                <option value="Card">Card</option>
                <option value="Bank Transfer">Bank Transfer</option>
                <option value="Cheque">Cheque</option>
              </select>
            </div>
            <div className="form-group">
              <label>Payment Account:</label>
              <select className="form-select" value={paymentEntry.paymentAccount} onChange={(e) => handlePaymentEntryChange('paymentAccount', e.target.value)}>
                <option value="">None</option>
              </select>
            </div>
          </div>
          <div className="form-row" style={{ marginTop: '0.5rem' }}>
            <div className="form-group">
              <label>Shipping Details:</label>
              <textarea className="form-textarea" rows="2" style={{ minHeight: '60px' }} value={form.shippingDetails} onChange={(e) => handleChange('shippingDetails', e.target.value)} />
            </div>
            <div className="form-group">
              <label>Payment Note:</label>
              <textarea className="form-textarea" rows="2" style={{ minHeight: '60px' }} value={paymentEntry.paymentNote} onChange={(e) => handlePaymentEntryChange('paymentNote', e.target.value)} />
            </div>
          </div>
          <div className="payment-summary" style={{ marginTop: '0.5rem', flexWrap: 'wrap', gap: '0.5rem' }}>
            <span style={{ flex: '1 1 auto', textAlign: 'center', whiteSpace: 'nowrap' }}>Purchase Total: $ {purchaseTotal.toFixed(2)}</span>
            <span style={{ flex: '1 1 auto', textAlign: 'center', whiteSpace: 'nowrap' }}>Change Return: $ {changeReturn.toFixed(2)}</span>
            <span style={{ flex: '1 1 auto', textAlign: 'center', whiteSpace: 'nowrap' }}>Payment Due: $ {paymentDue.toFixed(2)}</span>
          </div>

          <div className="payment-actions">
            <button type="button" className="add-payment-submit-btn" onClick={handleAddPayment}>
              + Add Payment
            </button>
          </div>

          {/* Payment entries list */}
          {payments.length > 0 && (
            <div className="payments-list">
              <h4>Payment Entries</h4>
              {payments.map((p) => (
                <div key={p.id} className="payment-entry" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr) auto', gap: '1rem', alignItems: 'center', padding: '1rem', border: '1px solid #e2e8f0', borderRadius: '8px', marginBottom: '0.5rem', background: '#f8fafc' }}>
                  <div>
                    <span style={{ display: 'block', fontSize: '0.75rem', color: '#64748b' }}>Amount</span>
                    <span style={{ fontWeight: '600' }}>$ {parseFloat(p.amount).toFixed(2)}</span>
                  </div>
                  <div>
                    <span style={{ display: 'block', fontSize: '0.75rem', color: '#64748b' }}>Paid on</span>
                    <span style={{ fontWeight: '500' }}>{p.paidOn}</span>
                  </div>
                  <div>
                    <span style={{ display: 'block', fontSize: '0.75rem', color: '#64748b' }}>Method</span>
                    <span style={{ fontWeight: '500' }}>{p.paymentMethod}</span>
                  </div>
                  <div>
                    <span style={{ display: 'block', fontSize: '0.75rem', color: '#64748b' }}>Account</span>
                    <span style={{ fontWeight: '500' }}>{p.paymentAccount || 'None'}</span>
                  </div>
                  <button type="button" className="remove-payment-btn" onClick={() => handleRemovePayment(p.id)}>Remove</button>
                  {p.paymentNote && (
                    <div style={{ gridColumn: '1 / -1', fontSize: '0.85rem', color: '#64748b' }}>
                      <strong>Note:</strong> {p.paymentNote}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Action Buttons ───────────────────────────────────────────────── */}
        <div className="form-section-content action-buttons-section">
          <div className="action-buttons">
            <button type="submit" className="save-btn">💾 Save</button>
            <button type="button" className="save-print-btn" onClick={() => { handleSave(); }}>🖨️ Save & Print</button>
          </div>
        </div>

      </form>

      {/* ── Add Vendor Modal ──────────────────────────────────────────────── */}
      {showAddVendorModal && (
        <AddVendorModal
          vendorForm={vendorForm}
          vendorFormErrors={vendorFormErrors}
          showVendorMoreInfo={showVendorMoreInfo}
          onClose={handleCloseVendorModal}
          onChange={handleVendorChange}
          onSubmit={handleVendorSubmit}
          onToggleMoreInfo={() => setShowVendorMoreInfo(v => !v)}
        />
      )}

      {/* ── Import Products Modal ─────────────────────────────────────────── */}
      {showImportModal && (
        <div className="modal-overlay" onClick={() => { setShowImportModal(false); setImportProductStatus(null); setImportFile(null); }}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '850px', width: '95%', borderRadius: '12px', overflow: 'hidden' }}>
            <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1.5rem', borderBottom: '1px solid #e2e8f0' }}>
              <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '600' }}>Import Products</h2>
              <button className="modal-close-btn" onClick={() => { setShowImportModal(false); setImportProductStatus(null); setImportFile(null); }}>✕</button>
            </div>

            <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.25rem', maxHeight: '80vh', overflowY: 'auto' }}>
              
              {/* Template Download */}
              <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                <button
                  type="button"
                  onClick={handleDownloadImportProductTemplate}
                  style={{ padding: '0.6rem 1.2rem', background: '#10b981', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '0.85rem', display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                  Download template file
                </button>
              </div>

              {/* Status Message */}
              {importProductStatus && (
                <div style={{ padding: '1rem', borderRadius: '8px', fontSize: '0.875rem', background: importProductStatus.type === 'success' ? '#f0fdf4' : '#fef2f2', color: importProductStatus.type === 'success' ? '#166534' : '#991b1b', border: `1px solid ${importProductStatus.type === 'success' ? '#bbf7d0' : '#fecaca'}` }}>
                  <div style={{ fontWeight: '600' }}>{importProductStatus.msg}</div>
                  {importProductStatus.data && (
                    <div style={{ marginTop: '0.5rem', display: 'flex', gap: '1rem' }}>
                      <span style={{background: importProductStatus.type === 'success' ? '#dcfce7' : '#fee2e2', padding: '0.1rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem'}}>Processed: {importProductStatus.data.total_products || 0}</span>
                      <span style={{background: importProductStatus.type === 'success' ? '#dcfce7' : '#fee2e2', padding: '0.1rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem'}}>Imported: {importProductStatus.data.imported || 0}</span>
                    </div>
                  )}
                  {importProductStatus.errors && importProductStatus.errors.length > 0 && (
                    <ul style={{ margin: '0.5rem 0 0', paddingLeft: '1.25rem', fontSize: '0.8rem' }}>
                      {importProductStatus.errors.map((err, i) => (
                        <li key={i}>Row {err.row}: {err.error}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              {/* File drop zone */}
              <div>
                <label style={{ fontWeight: '600', fontSize: '0.9rem', display: 'block', marginBottom: '0.5rem' }}>File To Import:</label>
                <div
                  onDragOver={(e) => { e.preventDefault(); setImportDragOver(true); }}
                  onDragLeave={() => setImportDragOver(false)}
                  onDrop={(e) => { e.preventDefault(); setImportDragOver(false); const f = e.dataTransfer.files[0]; if (f) setImportFile(f); }}
                  onClick={() => document.getElementById('import-modal-file').click()}
                  style={{
                    border: `2px dashed ${importDragOver ? '#3b82f6' : '#cbd5e1'}`,
                    borderRadius: '8px',
                    padding: '1.5rem',
                    textAlign: 'center',
                    cursor: 'pointer',
                    background: importDragOver ? '#eff6ff' : '#f8fafc',
                    transition: 'all 0.2s',
                    color: '#64748b',
                  }}
                >
                  <div style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>📁</div>
                  {importFile
                    ? <div style={{ color: '#1e293b', fontWeight: '500' }}>{importFile.name}</div>
                    : <div>Drop files here to upload<br /><span style={{ fontSize: '0.8rem' }}>or click to browse (.csv, .xlsx, .xls)</span></div>
                  }
                  <input type="file" id="import-modal-file" accept=".csv,.xlsx,.xls" style={{ display: 'none' }} onChange={(e) => { if (e.target.files[0]) setImportFile(e.target.files[0]); }} />
                </div>
              </div>

              {/* Instructions table */}
              <div>
                <div style={{ fontWeight: '600', fontSize: '0.9rem', marginBottom: '0.3rem' }}>Instructions:</div>
                <div style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '0.75rem' }}>Follow the instructions carefully before importing the file. The columns of the file should be in the following order.</div>
                <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                    <thead>
                      <tr style={{ background: '#f8fafc' }}>
                        <th style={{ padding: '0.6rem 0.85rem', borderBottom: '1px solid #e2e8f0', borderRight: '1px solid #e2e8f0', textAlign: 'center', width: '80px' }}>Column No</th>
                        <th style={{ padding: '0.6rem 0.85rem', borderBottom: '1px solid #e2e8f0', borderRight: '1px solid #e2e8f0', textAlign: 'left', width: '200px' }}>Column Name</th>
                        <th style={{ padding: '0.6rem 0.85rem', borderBottom: '1px solid #e2e8f0', textAlign: 'left' }}>Instruction</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { num: 1, name: 'Product Name (Required)', note: 'Name of the product' },
                        { num: 2, name: 'Brand (Optional)', note: 'Name of the brand (If not found new brand with the given name will be created)' },
                        { num: 3, name: 'Unit (Required)', note: 'Name of the unit' },
                        { num: 4, name: 'Category (Optional)', note: 'Name of the Category' },
                        { num: 5, name: 'Sub category (Optional)', note: 'Name of the Sub-Category' },
                        { num: 6, name: 'SKU (Optional)', note: 'Product SKU. If blank an SKU will be automatically generated' },
                        { num: 7, name: 'Barcode Type (Optional, Default: C128)', note: 'Barcode Type for the product' },
                        { num: 8, name: 'Manage Stock? (Required)', note: '1 = Yes; 0 = No' },
                        { num: 9, name: 'Alert quantity (Optional)', note: 'Alert quantity' },
                        { num: 10, name: 'Expires in (Optional)', note: 'Product expiry period (Only in numbers)' },
                        { num: 11, name: 'Expiry Period Unit (Optional)', note: 'Unit for the expiry period (days, months)' },
                        { num: 12, name: 'Applicable Tax (Optional)', note: 'Name of the Tax Rate' },
                        { num: 13, name: 'Selling Price Tax Type (Required)', note: 'inclusive, exclusive' },
                        { num: 14, name: 'Product Type (Required)', note: 'single, variable' },
                        { num: 15, name: 'Variation Name (Required if variable)', note: 'Name of the variation (Ex: Size, Color etc)' },
                        { num: 16, name: 'Variation Values (Required if variable)', note: "Values separated with '|' (Ex: Red|Blue|Green)" },
                        { num: 17, name: 'Variation SKUs (Optional)', note: "SKUs separated by '|' if variable" },
                        { num: 18, name: 'Purchase Price (Including Tax)', note: "Including Tax (Only in numbers)" },
                        { num: 19, name: 'Purchase Price (Excluding Tax)', note: "Excluding Tax (Only in numbers)" },
                        { num: 20, name: 'Profit Margin % (Optional)', note: 'Profit Margin (Only in numbers)' },
                        { num: 21, name: 'Selling Price (Optional)', note: 'Selling Price (Only in numbers)' },
                        { num: 22, name: 'Opening Stock (Optional)', note: "Opening Stock (Only in numbers)" },
                        { num: 23, name: 'Opening stock location (Optional)', note: 'Name of the business location' },
                        { num: 24, name: 'Expiry Date (Optional)', note: 'Format: mm-dd-yyyy; Ex: 11-25-2018' },
                        { num: 25, name: 'Enable description, IMEI or Serial (Optional)', note: '1 = Yes; 0 = No' },
                        { num: 26, name: 'Weight (Optional)', note: 'Optional' },
                        { num: 30, name: 'Image (Optional)', note: 'Image name or URL' },
                        { num: 31, name: 'Product Description (Optional)', note: '' },
                        { num: 37, name: 'Product locations (Optional)', note: 'Comma separated string of business location names' },
                      ].map(row => (
                        <tr key={row.num}>
                          <td style={{ padding: '0.5rem 0.85rem', borderBottom: '1px solid #e2e8f0', borderRight: '1px solid #e2e8f0', textAlign: 'center', color: '#64748b' }}>{row.num}</td>
                          <td style={{ padding: '0.5rem 0.85rem', borderBottom: '1px solid #e2e8f0', borderRight: '1px solid #e2e8f0', fontWeight: '500', color: '#1e293b' }}>{row.name}</td>
                          <td style={{ padding: '0.5rem 0.85rem', borderBottom: '1px solid #e2e8f0', color: '#64748b' }}>{row.note}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', padding: '1rem 1.5rem', borderTop: '1px solid #e2e8f0' }}>
              <button type="button" onClick={() => { setShowImportModal(false); setImportProductStatus(null); setImportFile(null); }} style={{ padding: '0.5rem 1.25rem', border: '1px solid #e2e8f0', borderRadius: '6px', background: 'white', cursor: 'pointer', fontWeight: '500' }}>Close</button>
              <button type="button" disabled={!importFile || isImportingProduct} onClick={handleImport} style={{ padding: '0.5rem 1.5rem', border: 'none', borderRadius: '6px', background: (importFile && !isImportingProduct) ? '#3b82f6' : '#94a3b8', color: 'white', cursor: (importFile && !isImportingProduct) ? 'pointer' : 'not-allowed', fontWeight: '600' }}>
                {isImportingProduct ? 'Importing...' : 'Import'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Attach Document Preview Modal ─────────────────────────────────── */}
      {showAttachDocModal && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '20px'
          }}
          onClick={() => setShowAttachDocModal(false)}
        >
          <div 
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              width: '95vw',
              height: '95vh',
              overflow: 'auto',
              position: 'relative',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              padding: '20px',
              borderBottom: '1px solid #e2e8f0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              position: 'sticky',
              top: 0,
              backgroundColor: 'white',
              zIndex: 1
            }}>
              <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '600', color: '#1e293b' }}>
                Attached Document Preview
              </h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                {form.attachDocument?.type.startsWith('image/') && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginRight: '10px' }}>
                    <button
                      onClick={() => setAttachDocZoom(prev => Math.max(0.5, prev - 0.25))}
                      style={{
                        background: '#f1f5f9',
                        border: '1px solid #cbd5e1',
                        borderRadius: '6px',
                        padding: '6px 12px',
                        cursor: 'pointer',
                        fontSize: '1rem',
                        fontWeight: '600',
                        color: '#475569'
                      }}
                      title="Zoom Out"
                    >
                      −
                    </button>
                    <span style={{ fontSize: '0.875rem', fontWeight: '500', color: '#64748b', minWidth: '50px', textAlign: 'center' }}>
                      {Math.round(attachDocZoom * 100)}%
                    </span>
                    <button
                      onClick={() => setAttachDocZoom(prev => Math.min(3, prev + 0.25))}
                      style={{
                        background: '#f1f5f9',
                        border: '1px solid #cbd5e1',
                        borderRadius: '6px',
                        padding: '6px 12px',
                        cursor: 'pointer',
                        fontSize: '1rem',
                        fontWeight: '600',
                        color: '#475569'
                      }}
                      title="Zoom In"
                    >
                      +
                    </button>
                    <button
                      onClick={() => setAttachDocZoom(1)}
                      style={{
                        background: '#f1f5f9',
                        border: '1px solid #cbd5e1',
                        borderRadius: '6px',
                        padding: '6px 12px',
                        cursor: 'pointer',
                        fontSize: '0.75rem',
                        fontWeight: '500',
                        color: '#475569'
                      }}
                      title="Reset Zoom"
                    >
                      Reset
                    </button>
                  </div>
                )}
                <button
                  onClick={() => { setShowAttachDocModal(false); setAttachDocZoom(1); }}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '1.5rem',
                    cursor: 'pointer',
                    color: '#64748b',
                    padding: '0',
                    width: '32px',
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '4px'
                  }}
                >
                  ×
                </button>
              </div>
            </div>
            <div style={{ padding: '20px', overflow: 'auto' }}>
              {attachDocPreview && form.attachDocument && (
                <div style={{ textAlign: 'center' }}>
                  {form.attachDocument.type.startsWith('image/') ? (
                    <img 
                      src={attachDocPreview} 
                      alt="Attached Document" 
                      style={{ 
                        maxWidth: '100%',
                        maxHeight: '80vh',
                        objectFit: 'contain',
                        borderRadius: '8px',
                        transform: `scale(${attachDocZoom})`,
                        transition: 'transform 0.2s ease',
                        cursor: attachDocZoom > 1 ? 'move' : 'default'
                      }} 
                    />
                  ) : form.attachDocument.type === 'application/pdf' ? (
                    <iframe
                      src={attachDocPreview}
                      style={{
                        width: '100%',
                        height: '80vh',
                        border: 'none',
                        borderRadius: '8px'
                      }}
                      title="PDF Preview"
                    />
                  ) : (form.attachDocument.type.startsWith('text/') || /\.(edi|x12|txt|csv|xml|json)$/i.test(form.attachDocument.name)) ? (
                    <div style={{ 
                      textAlign: 'left', 
                      background: '#f8fafc', 
                      padding: '20px', 
                      borderRadius: '8px', 
                      border: '1px solid #e2e8f0', 
                      overflow: 'auto', 
                      maxHeight: '75vh' 
                    }}>
                      <pre style={{ 
                        margin: 0, 
                        fontFamily: '"Cascadia Code", "Source Code Pro", Menlo, Monaco, Consolas, monospace', 
                        whiteSpace: 'pre-wrap', 
                        wordBreak: 'break-all', 
                        fontSize: '0.85rem',
                        color: '#334155',
                        lineHeight: '1.5'
                      }}>
                        {attachDocText || 'Loading content...'}
                      </pre>
                    </div>
                  ) : (
                    <div style={{ 
                      padding: '40px', 
                      textAlign: 'center',
                      color: '#64748b'
                    }}>
                      <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ margin: '0 auto 20px' }}>
                        <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"></path>
                        <polyline points="13 2 13 9 20 9"></polyline>
                      </svg>
                      <p style={{ fontSize: '1.125rem', fontWeight: '500', marginBottom: '10px' }}>
                        File uploaded successfully
                      </p>
                      <p style={{ fontSize: '0.875rem' }}>
                        <strong>Filename:</strong> {form.attachDocument.name}
                      </p>
                      <p style={{ fontSize: '0.875rem' }}>
                        <strong>Type:</strong> {form.attachDocument.type || 'Unknown'}
                      </p>
                      <p style={{ fontSize: '0.875rem' }}>
                        <strong>Size:</strong> {(form.attachDocument.size / 1024).toFixed(2)} KB
                      </p>
                      <p style={{ fontSize: '0.875rem', marginTop: '20px', color: '#94a3b8' }}>
                        Preview not available for this file type
                      </p>
                    </div>
                  )}
                  <div style={{ marginTop: '20px', fontSize: '0.875rem', color: '#64748b' }}>
                    <strong>File:</strong> {form.attachDocument.name}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Add Product Modal (Isolated) ──────────────────────────────────────── */}
      {showAddProductModal && (
        <div id="ap-new-product-modal" onClick={() => setShowAddProductModal(false)}>
          <style>{`
            #ap-new-product-modal {
              position: fixed;
              top: 0; left: 0; width: 100vw; height: 100vh;
              background: rgba(0,0,0,0.6);
              z-index: 999999;
              display: flex;
              justify-content: center;
              align-items: center;
            }
            #ap-new-product-modal-content {
              background: #fff;
              width: 95vw;
              max-width: 1400px;
              height: 95vh;
              border-radius: 12px;
              display: flex;
              flex-direction: column;
              animation: modalFadeIn 0.3s ease;
              box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
              overflow: hidden;
            }
            @keyframes modalFadeIn {
              from { opacity: 0; transform: scale(0.98); }
              to { opacity: 1; transform: scale(1); }
            }
            #ap-new-product-modal-header {
              padding: 1.2rem 1.5rem;
              border-bottom: 1px solid #e2e8f0;
              display: flex;
              justify-content: space-between;
              align-items: center;
              background: #f8fafc;
            }
            #ap-new-product-modal-header h2 {
              margin: 0;
              font-size: 1.25rem;
              color: #1e293b;
            }
            #ap-new-product-close {
              background: none;
              border: none;
              font-size: 1.5rem;
              cursor: pointer;
              color: #64748b;
            }
            #ap-new-product-close:hover {
              color: #0f172a;
            }
            #ap-new-product-modal-body {
              flex: 1;
              overflow-y: auto;
              padding: 0;
            }
            /* Strictly scoped overrides for the internal AddProduct form */
            #ap-new-product-modal-body .add-product-container {
              width: 100% !important;
              max-width: 100% !important;
              margin: 0 !important;
              padding: 1.5rem !important;
              box-shadow: none !important;
              border: none !important;
            }
            #ap-new-product-modal-body .product-form,
            #ap-new-product-modal-body .form-section {
              width: 100% !important;
              max-width: 100% !important;
            }
            #ap-new-product-modal-body .form-row {
              display: grid !important;
              grid-template-columns: repeat(3, 1fr) !important;
              gap: 1.25rem !important;
              width: 100% !important;
              margin-bottom: 1.25rem !important;
            }
            #ap-new-product-modal-body .description-row {
              display: grid !important;
              grid-template-columns: 2fr 1fr !important;
              gap: 1.5rem !important;
            }
            @media (max-width: 900px) {
              #ap-new-product-modal-body .form-row {
                grid-template-columns: repeat(2, 1fr) !important;
              }
              #ap-new-product-modal-body .description-row {
                grid-template-columns: 1fr !important;
              }
            }
            @media (max-width: 600px) {
              #ap-new-product-modal-body .form-row {
                grid-template-columns: 1fr !important;
              }
            }
          `}</style>
          <div id="ap-new-product-modal-content" onClick={(e) => e.stopPropagation()}>
            <div id="ap-new-product-modal-header">
              <h2>Add New Product</h2>
              <button id="ap-new-product-close" onClick={() => setShowAddProductModal(false)}>✕</button>
            </div>
            <div id="ap-new-product-modal-body">
              <AddProduct onSuccess={() => {
                setShowAddProductModal(false);
                loadProducts();
              }} />
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default AddPurchase;

